import React from 'react';
import PropTypes from 'prop-types';

export const cn = (...values) => values.filter(Boolean).join(' ');

const buttonVariants = {
  primary: 'bg-primary text-white shadow-sm hover:bg-primary/90',
  secondary: 'border border-border bg-surface text-foreground hover:bg-muted',
  outline:
    'border border-border bg-transparent text-foreground hover:border-primary hover:text-primary',
  ghost: 'text-muted-foreground hover:bg-muted hover:text-foreground',
  destructive: 'bg-destructive text-white shadow-sm hover:bg-destructive/90',
  success: 'bg-success text-white shadow-sm hover:bg-success/90'
};

const buttonSizes = {
  sm: 'h-8 rounded-md px-3 text-xs',
  md: 'h-10 rounded-lg px-4 text-sm',
  lg: 'h-11 rounded-lg px-5 text-sm'
};

export const Button = React.forwardRef(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      children,
      className,
      disabled = false,
      type = 'button',
      ...props
    },
    ref
  ) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-2 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
        buttonVariants[variant],
        buttonSizes[size],
        className
      )}
      disabled={loading || disabled}
      {...props}
    >
      {loading ? (
        <Skeleton className="h-3 w-12 rounded-sm bg-white/50" />
      ) : null}
      {children}
    </button>
  )
);

Button.displayName = 'Button';

export const IconButton = React.forwardRef(
  ({ label, size = 'md', className, children, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:pointer-events-none disabled:opacity-50',
        size === 'sm' ? 'h-8 w-8' : 'h-10 w-10',
        className
      )}
      {...props}
    >
      {children}
    </button>
  )
);

IconButton.displayName = 'IconButton';

export const Input = React.forwardRef(
  ({ label, error, helperText, className, id, ...props }, ref) => (
    <div className="w-full">
      {label ? (
        <label
          htmlFor={id}
          className="mb-1.5 block text-sm font-medium text-foreground"
        >
          {label}
        </label>
      ) : null}
      <input
        ref={ref}
        id={id}
        className={cn(
          'h-10 w-full rounded-lg border border-border bg-surface px-3 text-sm text-foreground placeholder:text-subtle focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15 disabled:cursor-not-allowed disabled:bg-muted',
          error &&
            'border-destructive focus:border-destructive focus:ring-destructive/15',
          className
        )}
        aria-invalid={Boolean(error)}
        {...props}
      />
      {error ? (
        <p className="mt-1.5 text-xs text-destructive">{error}</p>
      ) : null}
      {!error && helperText ? (
        <p className="mt-1.5 text-xs text-muted-foreground">{helperText}</p>
      ) : null}
    </div>
  )
);

Input.displayName = 'Input';

export const Textarea = React.forwardRef(
  ({ label, error, className, id, ...props }, ref) => (
    <div className="w-full">
      {label ? (
        <label
          htmlFor={id}
          className="mb-1.5 block text-sm font-medium text-foreground"
        >
          {label}
        </label>
      ) : null}
      <textarea
        ref={ref}
        id={id}
        className={cn(
          'min-h-24 w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder:text-subtle focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15',
          error && 'border-destructive',
          className
        )}
        aria-invalid={Boolean(error)}
        {...props}
      />
      {error ? (
        <p className="mt-1.5 text-xs text-destructive">{error}</p>
      ) : null}
    </div>
  )
);

Textarea.displayName = 'Textarea';

export const Card = ({ children, className, padding = 'md', ...props }) => (
  <section
    className={cn(
      'rounded-xl border border-border bg-surface shadow-card',
      padding === 'sm' ? 'p-4' : padding === 'lg' ? 'p-6' : 'p-5',
      className
    )}
    {...props}
  >
    {children}
  </section>
);

export const Badge = ({ children, variant = 'neutral', className }) => {
  const variants = {
    neutral: 'bg-muted text-muted-strong',
    primary: 'bg-primary/10 text-primary',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning-foreground',
    destructive: 'bg-destructive/10 text-destructive'
  };
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium',
        variants[variant],
        className
      )}
    >
      {children}
    </span>
  );
};

export const SegmentedControl = ({ items, value, onChange, className }) => (
  <div
    className={cn(
      'inline-flex flex-wrap items-center gap-1 rounded-lg bg-muted p-1',
      className
    )}
    role="tablist"
  >
    {items.map(item => {
      const itemValue = typeof item === 'string' ? item : item.value;
      const label = typeof item === 'string' ? item : item.label;
      const disabled = typeof item === 'string' ? false : item.disabled;
      const isActive = value === itemValue;
      return (
        <button
          key={itemValue}
          type="button"
          role="tab"
          aria-selected={isActive}
          aria-disabled={disabled || undefined}
          disabled={disabled}
          onClick={() => onChange(itemValue)}
          className={cn(
            'min-h-8 rounded-md px-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
            isActive
              ? 'bg-primary text-white shadow-card'
              : 'bg-transparent text-muted-strong hover:bg-surface/70 hover:text-foreground',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          {label}
        </button>
      );
    })}
  </div>
);

export const Skeleton = ({ className }) => (
  <div
    aria-hidden="true"
    className={cn('animate-pulse rounded-md bg-muted', className)}
  />
);

export const EmptyState = ({ title, description, action, icon, className }) => (
  <div
    className={cn(
      'flex w-full flex-col items-center justify-center rounded-xl border border-dashed border-border bg-surface px-6 py-12 text-center',
      className
    )}
  >
    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
      {icon || '∅'}
    </div>
    <h3 className="text-base font-semibold text-foreground">{title}</h3>
    {description ? (
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        {description}
      </p>
    ) : null}
    {action ? <div className="mt-5">{action}</div> : null}
  </div>
);

export const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'lg',
  className,
  closeDisabled = false
}) => {
  const dialogRef = React.useRef(null);
  const closeButtonRef = React.useRef(null);
  React.useEffect(() => {
    if (!isOpen) return undefined;
    const previouslyFocused = document.activeElement;
    const handleKeyDown = event => {
      if (event.key === 'Escape' && !closeDisabled) onClose();
      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusable = dialogRef.current.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    const focusFrame = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.cancelAnimationFrame(focusFrame);
      previouslyFocused?.focus?.();
    };
  }, [isOpen, onClose, closeDisabled]);

  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-9999 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ui-modal-title"
    >
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        disabled={closeDisabled}
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        className={cn(
          'relative flex max-h-[90vh] w-full flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-6',
          size === 'sm'
            ? 'max-w-md'
            : size === 'xl'
              ? 'max-w-6xl'
              : 'max-w-2xl',
          className
        )}
      >
        <header className="flex shrink-0 items-center justify-between border-b border-border px-5 py-4">
          <h2
            id="ui-modal-title"
            className="text-base font-semibold text-foreground"
          >
            {title}
          </h2>
          <IconButton
            ref={closeButtonRef}
            label="Close dialog"
            disabled={closeDisabled}
            size="sm"
            onClick={onClose}
          >
            ×
          </IconButton>
        </header>
        <div className="min-h-0 overflow-y-auto p-5">{children}</div>
        {footer ? (
          <footer className="flex shrink-0 justify-end gap-3 border-t border-border px-5 py-4">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
};

Button.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.string,
  size: PropTypes.string,
  loading: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  type: PropTypes.string
};
IconButton.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  size: PropTypes.string,
  className: PropTypes.string
};
Input.propTypes = {
  label: PropTypes.string,
  error: PropTypes.string,
  helperText: PropTypes.string,
  className: PropTypes.string,
  id: PropTypes.string
};
Textarea.propTypes = Input.propTypes;
Card.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  padding: PropTypes.string
};
Badge.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.string,
  className: PropTypes.string
};
SegmentedControl.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.node,
        disabled: PropTypes.bool
      })
    ])
  ).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string
};
Skeleton.propTypes = { className: PropTypes.string };
EmptyState.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  action: PropTypes.node,
  icon: PropTypes.node,
  className: PropTypes.string
};
Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  footer: PropTypes.node,
  size: PropTypes.string,
  className: PropTypes.string,
  closeDisabled: PropTypes.bool
};
